import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Plus, Upload, Download, Edit2, Trash2, X, Building2, AlertCircle } from 'lucide-react';
import { api } from '../../utils/api';

interface Dealer {
  id: string;
  province: string;
  city: string;
  area_manager: string;
  manufacturer: string;
  dealer_name: string;
  dealer_code: string;
  mailing_address: string;
  financial_advisor: string;
  phone: string;
  phone2: string;
  phone3: string;
  email1: string;
  email2: string;
  license_number: string;
  notes: string;
}

interface Filters {
  provinces: string[];
  cities: string[];
  manufacturers: string[];
}

// 经销商字段定义
const DEALER_FIELD_OPTIONS = [
  { key: 'province', label: '省份', required: false },
  { key: 'city', label: '城市', required: false },
  { key: 'area_manager', label: '区域经理', required: false },
  { key: 'manufacturer', label: '主机厂', required: false },
  { key: 'dealer_name', label: '经销商名称', required: true },
  { key: 'dealer_code', label: '经销商代码', required: false },
  { key: 'mailing_address', label: '收件地址', required: false },
  { key: 'financial_advisor', label: '金融顾问', required: false },
  { key: 'phone', label: '联系电话', required: false },
  { key: 'phone2', label: '联系电话2', required: false },
  { key: 'phone3', label: '联系电话3', required: false },
  { key: 'email1', label: '邮箱1', required: false },
  { key: 'email2', label: '邮箱2', required: false },
  { key: 'license_number', label: '执照编号', required: false },
  { key: 'notes', label: '备注', required: false },
];

const DealersAdminPage: React.FC = () => {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [filters, setFilters] = useState<Filters>({ provinces: [], cities: [], manufacturers: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [keyword, setKeyword] = useState('');

  // 编辑弹窗
  const [editingDealer, setEditingDealer] = useState<Dealer | null>(null);
  const [showModal, setShowModal] = useState(false);

  // 导入相关
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; failed: number; errors: string[] } | null>(null);

  // 列映射相关
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<{
    fileId: string;
    storageKey: string;
    filename: string;
    totalRows: number;
    headers: string[];
    preview: Record<string, string>[];
  } | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [customFields, setCustomFields] = useState<Array<{ key: string; label: string; required: boolean }>>([]);
  const [newFieldLabel, setNewFieldLabel] = useState('');

  // 表单状态
  const [formData, setFormData] = useState<Partial<Dealer>>({});

  // 加载数据
  const fetchDealers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (province) params.set('province', province);
      if (city) params.set('city', city);
      if (manufacturer) params.set('manufacturer', manufacturer);
      if (keyword) params.set('keyword', keyword);
      params.set('page', String(page));
      params.set('pageSize', '20');

      const response: any = await api.get(`/api/dealers?${params.toString()}`);
      if (response.success !== false) {
        setDealers(response.data || []);
        setFilters(response.filters || { provinces: [], cities: [], manufacturers: [] });
        setTotalPages(response.pagination?.totalPages || 1);
        setTotal(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('加载失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealers();
  }, [province, city, manufacturer, keyword, page]);

  const handleProvinceChange = (value: string) => {
    setProvince(value);
    setCity('');
    setPage(1);
  };

  // 打开编辑弹窗
  const openEdit = (dealer?: Dealer) => {
    if (dealer) {
      setEditingDealer(dealer);
      setFormData(dealer);
    } else {
      setEditingDealer(null);
      setFormData({
        province: '', city: '', area_manager: '', manufacturer: '',
        dealer_name: '', dealer_code: '', mailing_address: '',
        financial_advisor: '', phone: '', phone2: '', phone3: '',
        email1: '', email2: '', license_number: '', notes: '',
      });
    }
    setShowModal(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      if (editingDealer) {
        await api.put(`/api/dealers/${editingDealer.id}`, formData);
      } else {
        await api.post('/api/dealers', formData);
      }
      setShowModal(false);
      fetchDealers();
    } catch {
      alert('保存失败');
    }
  };

  // 删除
  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这条记录吗？')) return;
    try {
      await api.del(`/api/dealers/${id}`);
      fetchDealers();
    } catch {
      alert('删除失败');
    }
  };

  // 第一步：上传文件获取预览
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      const result: any = await response.json();

      if (result.fileId) {
        setUploadPreview({
          fileId: result.fileId,
          storageKey: result.storageKey,
          filename: result.filename,
          totalRows: result.totalRows,
          headers: result.headers,
          preview: result.preview,
        });

        // 自动匹配列
        const autoMapping: Record<string, string> = {};
        result.headers.forEach((header: string) => {
          const headerLower = header.toLowerCase();
          DEALER_FIELD_OPTIONS.forEach((field) => {
            if (headerLower.includes(field.label.toLowerCase()) || headerLower === field.key.toLowerCase()) {
              if (!autoMapping[field.key]) {
                autoMapping[field.key] = header;
              }
            }
          });
        });
        setColumnMapping(autoMapping);
        setShowMappingModal(true);
      } else {
        alert(result.error || '上传失败');
      }
    } catch {
      alert('上传失败');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // 第二步：确认导入
  const doImport = useCallback(async () => {
    if (!uploadPreview) return;

    // 验证必填字段
    if (!columnMapping['dealer_name'] && !columnMapping['dealer_code']) {
      alert('请至少选择"经销商名称"或"经销商代码"列');
      return;
    }

    setShowMappingModal(false);
    setImporting(true);

    try {
      const response = await fetch('/api/dealers/import-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId: uploadPreview.fileId,
          storageKey: uploadPreview.storageKey,
          columnMapping,
        }),
      });
      const result: any = await response.json();

      if (result.success) {
        setImportResult({ imported: result.imported, failed: result.failed, errors: result.errors || [] });
        fetchDealers();
      } else {
        alert(result.error || '导入失败');
      }
    } catch {
      alert('导入失败');
    } finally {
      setImporting(false);
      setUploadPreview(null);
    }
  }, [uploadPreview, columnMapping]);

  // 自定义字段管理
  const addCustomField = useCallback((label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const key = `custom_${Date.now()}_${trimmed}`;
    setCustomFields((prev) => [...prev, { key, label: trimmed, required: false }]);
    setNewFieldLabel('');
  }, []);

  const removeCustomField = useCallback((key: string) => {
    setCustomFields((prev) => prev.filter((f) => f.key !== key));
    setColumnMapping((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const allFieldOptions = [...DEALER_FIELD_OPTIONS, ...customFields];

  // 导出
  const handleExport = () => {
    const params = new URLSearchParams();
    if (province) params.set('province', province);
    if (city) params.set('city', city);
    if (manufacturer) params.set('manufacturer', manufacturer);
    window.open(`/api/dealers/import-export?${params.toString()}`, '_blank');
  };

  // 导出 VCF
  const handleExportVcf = () => {
    const params = new URLSearchParams();
    if (province) params.set('province', province);
    if (city) params.set('city', city);
    if (manufacturer) params.set('manufacturer', manufacturer);
    if (keyword) params.set('keyword', keyword);
    window.open(`/api/dealers/export-vcf?${params.toString()}`, '_blank');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">经销商管理</h1>
        <button
          onClick={() => openEdit()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          新增经销商
        </button>
      </div>

      {/* 导入结果提示 */}
      {importResult && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-green-800">
                导入完成！成功 {importResult.imported} 条，失败 {importResult.failed} 条
              </p>
              {importResult.errors.length > 0 && (
                <div className="mt-2 text-sm text-green-700">
                  {importResult.errors.map((err, i) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setImportResult(null)} className="text-green-600 hover:text-green-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* 筛选 */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <select
              value={province}
              onChange={(e) => handleProvinceChange(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">全部省份</option>
              {filters.provinces.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={city}
              onChange={(e) => { setCity(e.target.value); setPage(1); }}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">全部城市</option>
              {filters.cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={manufacturer}
              onChange={(e) => { setManufacturer(e.target.value); setPage(1); }}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">全部主机厂</option>
              {filters.manufacturers.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <input
              type="text"
              value={keyword}
              onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
              placeholder="搜索关键词..."
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls,.csv"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {importing ? '导入中...' : '导入'}
            </button>
            <button
              onClick={handleExport}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <p className="text-sm text-gray-500">
            共 <span className="font-medium">{total}</span> 条记录
          </p>
          <button
            onClick={handleExportVcf}
            className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm"
          >
            <Download className="w-4 h-4" />
            导出 VCF 通讯录
          </button>
        </div>
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">经销商</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">区域</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">主机厂</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">金融顾问</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">电话</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">加载中...</td>
                </tr>
              ) : dealers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">没有数据</td>
                </tr>
              ) : (
                dealers.map((dealer) => (
                  <tr key={dealer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{dealer.dealer_name}</div>
                      <div className="text-sm text-gray-500">{dealer.dealer_code}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {dealer.province} {dealer.city}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {dealer.manufacturer}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>{dealer.financial_advisor}</div>
                      <div className="text-sm text-gray-500">{dealer.area_manager}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>{dealer.phone}</div>
                      {dealer.phone2 && <div className="text-gray-500">{dealer.phone2}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(dealer)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(dealer.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t">
            <button onClick={() => setPage(1)} disabled={page === 1} className="px-3 py-1 rounded border disabled:opacity-50">首页</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 rounded border disabled:opacity-50">上一页</button>
            <span className="px-4 py-1">第 {page} / {totalPages} 页</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 rounded border disabled:opacity-50">下一页</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-3 py-1 rounded border disabled:opacity-50">末页</button>
          </div>
        )}
      </div>

      {/* 列映射弹窗 */}
      {showMappingModal && uploadPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">配置列映射</h2>
                <p className="text-sm text-gray-500">
                  文件：{uploadPreview.filename}（共 {uploadPreview.totalRows} 行数据）
                </p>
              </div>
              <button onClick={() => { setShowMappingModal(false); setUploadPreview(null); }} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* 数据预览 */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">数据预览（前3行）</h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {uploadPreview.headers.map((header) => (
                          <th key={header} className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadPreview.preview.map((row, i) => (
                        <tr key={i} className="border-b last:border-0">
                          {uploadPreview.headers.map((header) => (
                            <td key={header} className="px-3 py-2 text-gray-600 whitespace-nowrap">
                              {row[header] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 列映射配置 */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">列映射配置</h3>
                <div className="space-y-3">
                  {allFieldOptions.map((field) => (
                    <div key={field.key} className="flex items-center gap-3">
                      <label className="w-28 text-sm font-medium text-gray-700 flex items-center shrink-0">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <select
                        value={columnMapping[field.key] || ''}
                        onChange={(e) => {
                          setColumnMapping((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- 不导入 --</option>
                        {uploadPreview.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                      {field.key.startsWith('custom_') && (
                        <button
                          onClick={() => removeCustomField(field.key)}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 添加自定义字段 */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">添加自定义字段</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFieldLabel}
                    onChange={(e) => setNewFieldLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addCustomField(newFieldLabel); }
                    }}
                    placeholder="输入字段名称..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={() => addCustomField(newFieldLabel)}
                    disabled={!newFieldLabel.trim()}
                    className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    + 添加
                  </button>
                </div>
              </div>

              {/* 提示 */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <p>请确认每列映射正确，至少需要映射"经销商名称"或"经销商代码"。未映射的列将被忽略。</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => { setShowMappingModal(false); setUploadPreview(null); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={doImport}
                disabled={importing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                {importing ? '导入中...' : '确认导入'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingDealer ? '编辑经销商' : '新增经销商'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-60px)]">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">省份 *</label>
                  <input type="text" value={formData.province || ''} onChange={(e) => setFormData({ ...formData, province: e.target.value })} className="w-full border rounded-lg px-3 py-2" placeholder="广西壮族自治区" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">城市 *</label>
                  <input type="text" value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full border rounded-lg px-3 py-2" placeholder="南宁市" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">区域经理</label>
                  <input type="text" value={formData.area_manager || ''} onChange={(e) => setFormData({ ...formData, area_manager: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">主机厂 *</label>
                  <input type="text" value={formData.manufacturer || ''} onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })} className="w-full border rounded-lg px-3 py-2" placeholder="广汽埃安" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">经销商名称 *</label>
                  <input type="text" value={formData.dealer_name || ''} onChange={(e) => setFormData({ ...formData, dealer_name: e.target.value })} className="w-full border rounded-lg px-3 py-2" placeholder="南宁XX汽车销售服务有限公司" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">经销商代码</label>
                  <input type="text" value={formData.dealer_code || ''} onChange={(e) => setFormData({ ...formData, dealer_code: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">金融顾问</label>
                  <input type="text" value={formData.financial_advisor || ''} onChange={(e) => setFormData({ ...formData, financial_advisor: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">联系电话 *</label>
                  <input type="text" value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full border rounded-lg px-3 py-2" placeholder="138xxxx" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">备用电话</label>
                  <input type="text" value={formData.phone2 || ''} onChange={(e) => setFormData({ ...formData, phone2: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">第三个电话</label>
                  <input type="text" value={formData.phone3 || ''} onChange={(e) => setFormData({ ...formData, phone3: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">邮箱1</label>
                  <input type="email" value={formData.email1 || ''} onChange={(e) => setFormData({ ...formData, email1: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">邮箱2</label>
                  <input type="email" value={formData.email2 || ''} onChange={(e) => setFormData({ ...formData, email2: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">执照编号</label>
                  <input type="text" value={formData.license_number || ''} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">收件地址</label>
                  <input type="text" value={formData.mailing_address || ''} onChange={(e) => setFormData({ ...formData, mailing_address: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                  <textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full border rounded-lg px-3 py-2 h-20" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">取消</button>
                <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealersAdminPage;
